/**
 * Care-strategy resolution — the SINGLE canonical merge of the company's Care
 * configuration cascade:
 *
 *   Country default  ←  Company  ←  Program  ←  Cohort
 *
 * Each layer is a partial override; the most specific (Cohort) wins per field.
 * The resolved `CareStrategyConfig` drives the active pillars, checkpoint
 * cadence, feedback cadence, and the intervention thresholds the Care services
 * and the Commitment Index risk signals read — so a company's saved strategy
 * actually shapes the talent experience instead of being persisted-then-ignored.
 *
 * Pure + framework-agnostic so platform-api (resolution) and the FE (preview of
 * the effective config) compute the same result.
 */

export interface CheckpointScheduleConfig {
  preMove: Array<{ type: string; milesReward: number }>;
  postArrival: Array<{ type: string; daysAfterArrival: number; milesReward: number }>;
}

export interface CareInterventionThresholds {
  /** Commitment index at/below which the cohort surfaces the talent for action. */
  commitmentAtRisk: number;
  /** Pillar score (1–5) at/below which a pillar is flagged as a concern. */
  pillarConcern: number;
}

/**
 * How an objective is measured (mirrors `@tonnie/schemas` careObjectiveMeasureSchema;
 * defined locally to keep `@tonnie/heroes` dependency-free).
 */
export interface CareObjectiveMeasureSpec {
  type: 'number' | 'scale' | 'percent' | 'milestone' | 'rating';
  unit?: string;
  direction?: 'increase' | 'decrease' | 'reach';
  min?: number;
  max?: number;
  scaleLabels?: string[];
  defaultBaseline?: number;
  defaultTarget?: number;
}

/** A resolved talent objective (catalog id + title + category + measure + resolved baseline/target). */
export interface CareResolvedObjective {
  id: string;
  title: string;
  category?: string;
  /** How it's measured. Absent → legacy `rating`. */
  measure?: CareObjectiveMeasureSpec;
  /** Resolved baseline/target = cohort override ?? measure default. */
  baseline?: number;
  target?: number;
}

export interface CareStrategyConfig {
  /** Pillar codes that are active/prioritised for this scope. */
  activePillars: string[];
  /**
   * Subset of `activePillars` the talent CANNOT hide (company-mandated). The
   * talent may still add personal pillars and hide company-but-not-required
   * ones — but a required pillar is always present in the resolved talent set.
   * Defaults to `[]` (nothing forced) when no layer sets it.
   */
  requiredPillars: string[];
  checkpointSchedule: CheckpointScheduleConfig;
  feedbackCadence: string;
  interventionThresholds: CareInterventionThresholds;
  /**
   * Resolved objectives that apply to this scope. The company enables a library
   * (`enabledObjectives` + `objectivesCatalog`); a Program/Cohort may narrow it
   * (`selectedObjectives`). When no scope narrows, ALL company-enabled objectives
   * apply. Materialized per-talent into `care_objectives`.
   */
  objectives: CareResolvedObjective[];
  /**
   * Business roles that provide objective feedback at the cadence (e.g.
   * `['admin','team_member']`). Drives who the feedback cycle requests from.
   */
  feedbackProviders: string[];
}

/**
 * A single layer of the cascade. Tolerates BOTH the canonical field names AND
 * the looser shape the `CompanyCareConfig` UI persists (`pillars`,
 * `feedback.cadence`, `checkpointSchedule` as a string preset or object).
 */
export interface CareStrategyLayer {
  activePillars?: string[] | null;
  /** Loose alias used by the company config UI. */
  pillars?: string[] | null;
  /** Pillar codes the talent cannot hide (company-mandated). */
  requiredPillars?: string[] | null;
  checkpointSchedule?: string | (Partial<CheckpointScheduleConfig> & { presetId?: string }) | null;
  feedbackCadence?: string | null;
  /** Loose alias: `{ cadence, providers }` from the company config UI. */
  feedback?: { cadence?: string | null; providers?: string[] | null } | null;
  interventionThresholds?: Partial<CareInterventionThresholds> | null;
  /** Company library — objective ids enabled for use (the "available" set). */
  enabledObjectives?: string[] | null;
  /** Company catalog: id → text/category/measure (carries how each objective is measured). */
  objectivesCatalog?: Array<{
    id: string;
    text?: string | null;
    title?: string | null;
    category?: string | null;
    measure?: CareObjectiveMeasureSpec | null;
  }> | null;
  /** Program/Cohort: which enabled objectives apply to this scope (subset). */
  selectedObjectives?: string[] | null;
  /** Program/Cohort: per-objective baseline/target defaults (the hybrid cohort default). */
  objectiveTargets?: Record<string, { baseline?: number | null; target?: number | null }> | null;
  /** Business roles that provide objective feedback. */
  feedbackProviders?: string[] | null;
}

/**
 * The canonical Care pillar ids — the ONLY pillars the engine scores (matches the
 * brain `care_config` pillar catalog + checkpoint `pillarScores` keys). Everything
 * that persists or displays a pillar must use these ids.
 */
export const CANONICAL_CARE_PILLAR_IDS = [
  'housing_stability',
  'schooling_childcare',
  'work_fit',
  'wellbeing_social',
] as const;

export type CanonicalCarePillarId = (typeof CANONICAL_CARE_PILLAR_IDS)[number];

const CANONICAL_PILLAR_SET = new Set<string>(CANONICAL_CARE_PILLAR_IDS);

/**
 * Legacy business-UI pillar ids → canonical ids. The early Care settings UI used
 * short, non-canonical ids (`housing`, `work`, `social`, `wellbeing`) that never
 * matched the engine catalog, so a saved config silently failed to resolve. This
 * map lets the cascade interpret already-stored configs and lets the UIs migrate
 * their option ids. `social` + `wellbeing` both fold into the combined
 * `wellbeing_social` pillar. Legacy ids with NO canonical pillar (`financial`,
 * `family`, `legal`, `language`) are intentionally absent — they were never
 * scored, so they're dropped on canonicalization rather than faked.
 */
export const LEGACY_CARE_PILLAR_ID_MAP: Record<string, CanonicalCarePillarId> = {
  housing: 'housing_stability',
  work: 'work_fit',
  social: 'wellbeing_social',
  wellbeing: 'wellbeing_social',
};

/**
 * Normalise a pillar-id list to canonical ids: map known legacy aliases, keep
 * ids that are already canonical, drop everything else (legacy ids with no
 * engine pillar), and de-duplicate while preserving order. Pure + idempotent —
 * `canonicalize(canonicalize(x)) === canonicalize(x)`.
 */
export function canonicalizeCarePillarIds(ids: readonly unknown[] | null | undefined): string[] {
  if (!Array.isArray(ids)) return [];
  const out: string[] = [];
  for (const raw of ids) {
    if (typeof raw !== 'string') continue;
    const mapped = LEGACY_CARE_PILLAR_ID_MAP[raw] ?? (CANONICAL_PILLAR_SET.has(raw) ? raw : null);
    if (mapped && !out.includes(mapped)) out.push(mapped);
  }
  return out;
}

/** The canonical country-default strategy (matches the Care services' defaults). */
export const DEFAULT_CARE_STRATEGY: CareStrategyConfig = {
  activePillars: [...CANONICAL_CARE_PILLAR_IDS],
  // No country-default forces a pillar; companies opt pillars into "required".
  requiredPillars: [],
  checkpointSchedule: {
    preMove: [{ type: 'pre_move_baseline', milesReward: 25 }],
    postArrival: [
      { type: 'month_1', daysAfterArrival: 30, milesReward: 50 },
      { type: 'month_3', daysAfterArrival: 90, milesReward: 50 },
      { type: 'month_6', daysAfterArrival: 180, milesReward: 50 },
      { type: 'month_12', daysAfterArrival: 365, milesReward: 50 },
    ],
  },
  feedbackCadence: 'monthly',
  // Defaults mirror the canonical risk-signal cut-offs: the top of the "At Risk"
  // band (49) and the isolation/pillar-drop floor (≤2).
  interventionThresholds: { commitmentAtRisk: 49, pillarConcern: 2 },
  // No country-default objectives — the company opts objectives into its library.
  objectives: [],
  // Both canonical providers by default (matches the CompanyCareConfig defaults).
  feedbackProviders: ['admin', 'team_member'],
};

/**
 * Canonical checkpoint-schedule presets (the SAME schedules the CompanyCareConfig
 * UI offers). The CareCustomizeModal persists a program/cohort schedule override
 * as a bare preset id (`"standard"`), and the company config persists a structured
 * object that also carries `presetId` — `normalizeLayer` expands either form to
 * the canonical schedule so the override actually applies. Keep in lockstep with
 * `checkpointSchedules` in `apps/business-web/.../care/components/CompanyCareConfig.tsx`.
 */
export const CARE_CHECKPOINT_PRESETS: Record<string, CheckpointScheduleConfig> = {
  light: {
    preMove: [{ type: 'pre_move_baseline', milesReward: 25 }],
    postArrival: [
      { type: 'month_3', daysAfterArrival: 90, milesReward: 50 },
      { type: 'month_12', daysAfterArrival: 365, milesReward: 50 },
    ],
  },
  standard: {
    preMove: [{ type: 'pre_move_baseline', milesReward: 25 }],
    postArrival: [
      { type: 'month_1', daysAfterArrival: 30, milesReward: 50 },
      { type: 'month_3', daysAfterArrival: 90, milesReward: 50 },
      { type: 'month_6', daysAfterArrival: 180, milesReward: 50 },
      { type: 'month_12', daysAfterArrival: 365, milesReward: 50 },
    ],
  },
  intensive: {
    preMove: [{ type: 'pre_move_baseline', milesReward: 25 }],
    postArrival: [
      { type: 'month_1', daysAfterArrival: 30, milesReward: 50 },
      { type: 'month_3', daysAfterArrival: 90, milesReward: 50 },
      { type: 'month_6', daysAfterArrival: 180, milesReward: 50 },
      { type: 'month_9', daysAfterArrival: 270, milesReward: 50 },
      { type: 'month_12', daysAfterArrival: 365, milesReward: 50 },
    ],
  },
};

/**
 * Normalise a loose layer into a strict partial — resolving the company-UI
 * aliases (`pillars`, `feedback.cadence`) and dropping empty/preset-only
 * checkpoint schedules (a `presetId` with no arrays carries no usable override).
 */
type NormalizedLayer = {
  activePillars?: string[];
  requiredPillars?: string[];
  checkpointSchedule?: CheckpointScheduleConfig;
  feedbackCadence?: string;
  interventionThresholds?: Partial<CareInterventionThresholds>;
  /** id → {title, category, measure} from this layer's objectivesCatalog (company sets it). */
  objectiveCatalog?: Map<
    string,
    { title: string; category?: string; measure?: CareObjectiveMeasureSpec }
  >;
  /** Company-enabled objective ids (the available set). */
  availableObjectives?: string[];
  /** Program/Cohort selection (subset of available). */
  selectedObjectives?: string[];
  /** Program/Cohort per-objective baseline/target defaults. */
  objectiveTargets?: Map<string, { baseline?: number; target?: number }>;
  feedbackProviders?: string[];
};

function stringArray(v: unknown): string[] | undefined {
  if (!Array.isArray(v)) return undefined;
  return v.filter((x): x is string => typeof x === 'string');
}

function normalizeLayer(layer: CareStrategyLayer | null | undefined): NormalizedLayer {
  if (!layer) return {};
  const out: NormalizedLayer = {};

  // Canonicalize pillar ids at the cascade boundary so any stored layer — old
  // (short ids like `housing`/`social`) or new — resolves to the engine's
  // canonical ids. No data migration needed; stored configs self-heal on read.
  const pillars = layer.activePillars ?? layer.pillars;
  if (Array.isArray(pillars) && pillars.length > 0) {
    const canonical = canonicalizeCarePillarIds(pillars);
    if (canonical.length > 0) out.activePillars = canonical;
  }

  // requiredPillars is an empty-array-meaningful override: a layer that sets it
  // to [] is explicitly clearing the mandate, so we accept any array (not just
  // non-empty). Absent → inherit.
  if (Array.isArray(layer.requiredPillars)) {
    out.requiredPillars = canonicalizeCarePillarIds(layer.requiredPillars);
  }

  // checkpointSchedule arrives in one of three forms: a canonical structured
  // object ({preMove, postArrival}), an object carrying only a `presetId`, or a
  // bare preset string (the CareCustomizeModal persists `"standard"`). Expand a
  // preset to its canonical schedule so a program/cohort schedule override
  // actually applies instead of being silently dropped. Stored configs self-heal
  // on read — no migration needed.
  const schedule = layer.checkpointSchedule;
  if (typeof schedule === 'string') {
    const preset = CARE_CHECKPOINT_PRESETS[schedule];
    if (preset) out.checkpointSchedule = preset;
  } else if (schedule && typeof schedule === 'object') {
    const preMove = schedule.preMove;
    const postArrival = schedule.postArrival;
    if (Array.isArray(preMove) && Array.isArray(postArrival)) {
      out.checkpointSchedule = { preMove, postArrival };
    } else if (typeof schedule.presetId === 'string') {
      const preset = CARE_CHECKPOINT_PRESETS[schedule.presetId];
      if (preset) out.checkpointSchedule = preset;
    }
  }

  const cadence = layer.feedbackCadence ?? layer.feedback?.cadence;
  if (typeof cadence === 'string' && cadence.length > 0) out.feedbackCadence = cadence;

  if (layer.interventionThresholds) {
    const t = layer.interventionThresholds;
    const thresholds: Partial<CareInterventionThresholds> = {};
    if (typeof t.commitmentAtRisk === 'number') thresholds.commitmentAtRisk = t.commitmentAtRisk;
    if (typeof t.pillarConcern === 'number') thresholds.pillarConcern = t.pillarConcern;
    if (Object.keys(thresholds).length > 0) out.interventionThresholds = thresholds;
  }

  // Objectives catalog (id → title/category/measure) — the company layer carries it.
  if (Array.isArray(layer.objectivesCatalog) && layer.objectivesCatalog.length > 0) {
    const m = new Map<
      string,
      { title: string; category?: string; measure?: CareObjectiveMeasureSpec }
    >();
    for (const o of layer.objectivesCatalog) {
      if (!o || typeof o.id !== 'string') continue;
      const title =
        (typeof o.title === 'string' && o.title) || (typeof o.text === 'string' && o.text) || o.id;
      m.set(o.id, {
        title,
        category: typeof o.category === 'string' ? o.category : undefined,
        measure: o.measure ?? undefined,
      });
    }
    if (m.size > 0) out.objectiveCatalog = m;
  }
  const available = stringArray(layer.enabledObjectives);
  if (available) out.availableObjectives = available;
  const selected = stringArray(layer.selectedObjectives);
  if (selected) out.selectedObjectives = selected;

  // Per-objective baseline/target defaults (Program/Cohort hybrid default).
  if (layer.objectiveTargets && typeof layer.objectiveTargets === 'object') {
    const tm = new Map<string, { baseline?: number; target?: number }>();
    for (const [id, v] of Object.entries(layer.objectiveTargets)) {
      if (!v) continue;
      const entry: { baseline?: number; target?: number } = {};
      if (typeof v.baseline === 'number') entry.baseline = v.baseline;
      if (typeof v.target === 'number') entry.target = v.target;
      if (entry.baseline !== undefined || entry.target !== undefined) tm.set(id, entry);
    }
    if (tm.size > 0) out.objectiveTargets = tm;
  }

  const providers = stringArray(layer.feedbackProviders ?? layer.feedback?.providers);
  if (providers && providers.length > 0) out.feedbackProviders = providers;

  return out;
}

/**
 * Resolve the effective Care strategy by overlaying the cascade layers
 * (most-specific last). Country default is always the base; each subsequent
 * layer overrides only the fields it sets.
 */
export function resolveCareStrategy(
  ...layers: Array<CareStrategyLayer | null | undefined>
): CareStrategyConfig {
  let resolved: CareStrategyConfig = {
    activePillars: [...DEFAULT_CARE_STRATEGY.activePillars],
    requiredPillars: [...DEFAULT_CARE_STRATEGY.requiredPillars],
    checkpointSchedule: {
      preMove: [...DEFAULT_CARE_STRATEGY.checkpointSchedule.preMove],
      postArrival: [...DEFAULT_CARE_STRATEGY.checkpointSchedule.postArrival],
    },
    feedbackCadence: DEFAULT_CARE_STRATEGY.feedbackCadence,
    interventionThresholds: { ...DEFAULT_CARE_STRATEGY.interventionThresholds },
    objectives: [],
    feedbackProviders: [...DEFAULT_CARE_STRATEGY.feedbackProviders],
  };

  // Objectives resolve from a catalog (company) + an available set (company) +
  // an optional selection (the most-specific scope that narrows). Accumulated
  // separately because the final objective list is computed AFTER the cascade.
  let objectiveCatalog = new Map<
    string,
    { title: string; category?: string; measure?: CareObjectiveMeasureSpec }
  >();
  let availableObjectives: string[] | undefined;
  let selectedObjectives: string[] | undefined;
  // Per-objective baseline/target defaults — merged across layers (most-specific
  // scope wins per objective id).
  const objectiveTargets = new Map<string, { baseline?: number; target?: number }>();

  for (const layer of layers) {
    const n = normalizeLayer(layer);
    resolved = {
      ...resolved,
      activePillars: n.activePillars ?? resolved.activePillars,
      requiredPillars: n.requiredPillars ?? resolved.requiredPillars,
      checkpointSchedule: n.checkpointSchedule ?? resolved.checkpointSchedule,
      feedbackCadence: n.feedbackCadence ?? resolved.feedbackCadence,
      interventionThresholds: {
        commitmentAtRisk:
          n.interventionThresholds?.commitmentAtRisk ??
          resolved.interventionThresholds.commitmentAtRisk,
        pillarConcern:
          n.interventionThresholds?.pillarConcern ?? resolved.interventionThresholds.pillarConcern,
      },
      feedbackProviders: n.feedbackProviders ?? resolved.feedbackProviders,
    };
    if (n.objectiveCatalog) objectiveCatalog = n.objectiveCatalog;
    if (n.availableObjectives !== undefined) availableObjectives = n.availableObjectives;
    if (n.selectedObjectives !== undefined) selectedObjectives = n.selectedObjectives;
    if (n.objectiveTargets) {
      for (const [id, v] of n.objectiveTargets) objectiveTargets.set(id, v);
    }
  }

  // Applied objective ids = the narrowest selection that was set, else all
  // company-enabled. Keep only ids the catalog can title (so a stray id can't
  // produce a titleless objective), preserving order + de-duping.
  const appliedIds = selectedObjectives ?? availableObjectives ?? [];
  const seen = new Set<string>();
  const objectives: CareResolvedObjective[] = [];
  for (const id of appliedIds) {
    if (seen.has(id)) continue;
    const c = objectiveCatalog.get(id);
    if (!c) continue;
    seen.add(id);
    // Resolved baseline/target = the cohort/program override ?? the measure default.
    const override = objectiveTargets.get(id);
    const baseline = override?.baseline ?? c.measure?.defaultBaseline;
    const target = override?.target ?? c.measure?.defaultTarget;
    objectives.push({
      id,
      title: c.title,
      category: c.category,
      measure: c.measure,
      baseline,
      target,
    });
  }
  resolved.objectives = objectives;

  return resolved;
}
